import { useMutation } from '@apollo/client/react';
import {
	type MergeProjectCartsToSessionCartMutation,
	type MergeProjectCartsToSessionCartMutationVariables,
	type ProjectGroupItemSource,
	type ProjectItemFieldsFragment
} from '../../__generated__/graphql-client-types';
import { ITEM_ADDED_TO_CART, ITEM_ADDED_TO_CART_FAIL, ITEM_ADDED_TO_CART_INELIGIBLE } from '../../constants/message';
import { TrackedEvent, TrackedEventCase } from '../../helpers/analytics/event-types';
import { GTMCustomProductDimensions } from '../../helpers/analytics/gtm/gtm-types';
import { isShowroomOnly } from '../../helpers/project/project.helper';
import { CART_HEADER } from '../../queries/cart/cart-header.queries';
import { MERGE_PROJECT_GROUP_SOURCES } from '../../queries/project/project-details.queries';
import { type ProjectDetailsGroupItem } from '../../types/project.types';
import { useTrackEvent } from '../analytics/analytics.hooks';
import { useNotification } from '../notification/notification.hooks';
import { useProjectId } from './project-details.hooks';
import { useProjectGroups } from './project-group.hooks';

export const useProjectCartMutations = () => {
	const projectId = useProjectId();
	const groups = useProjectGroups();
	const { notifySuccess, notifyDanger } = useNotification();

	const [mergeProjectCartsToSessionCart, { loading: addToCartLoading }] = useMutation<
		MergeProjectCartsToSessionCartMutation,
		MergeProjectCartsToSessionCartMutationVariables
	>(MERGE_PROJECT_GROUP_SOURCES, { refetchQueries: [{ query: CART_HEADER }] });
	const trackEvent = useTrackEvent();

	return {
		addItemToCart: (item: ProjectDetailsGroupItem, groupId: number) => {
			const sourceItem: ProjectGroupItemSource = {
				itemId: item.id,
				status: item.quantityPurchased > 0 ? 'PURCHASED' : 'UNPURCHASED'
			};

			const sources = [
				{
					shoppingListGroupId: groupId,
					allItems: false,
					sourceItems: [sourceItem]
				}
			];

			mergeProjectCartsToSessionCart({ variables: { input: { projectId, sources } } })
				.then(() => {
					notifySuccess(ITEM_ADDED_TO_CART);
					trackEvent(buildProjectsAddToCartAnalytics([item]));
				})
				.catch(() => notifyDanger(ITEM_ADDED_TO_CART_FAIL));
		},
		addAllToCart: () => {
			const allItemsShowroomOnly: boolean = groups.every((group) => {
				const products = (group.pending || []).concat(group.purchased || []);
				return products.every((product) => isShowroomOnly(product.productPricingDisplay));
			});
			if (allItemsShowroomOnly) {
				notifyDanger(ITEM_ADDED_TO_CART_INELIGIBLE);
				return;
			}

			const sources = groups.map((group) => {
				const products = (group.pending || []).concat(group.purchased || []);
				const hasShowroomOnlyProduct = products.some((product) => isShowroomOnly(product.productPricingDisplay));
				return {
					shoppingListGroupId: group.id,
					allItems: !hasShowroomOnlyProduct,
					sourceItems: !hasShowroomOnlyProduct
						? undefined
						: products
								.filter((product) => !isShowroomOnly(product.productPricingDisplay))
								.filter((product) => product.type !== 'SPECIAL_PRODUCT')
								.map(({ id, quantityPurchased }) => {
									const item: ProjectGroupItemSource = {
										itemId: id,
										status: quantityPurchased > 0 ? 'PURCHASED' : 'UNPURCHASED'
									};
									return item;
								})
				};
			});

			const filteredSources = sources.filter(
				(source) => source.allItems || (!source.allItems && source.sourceItems && source.sourceItems.length > 0)
			);

			void mergeProjectCartsToSessionCart({ variables: { input: { projectId, sources: filteredSources } } })
				.then(() => {
					notifySuccess(ITEM_ADDED_TO_CART);
					trackEvent(
						buildProjectsAddToCartAnalytics(
							groups.reduce<(ProjectDetailsGroupItem | ProjectItemFieldsFragment)[]>(
								(acc, group) => (group.pending ? [...acc, ...group.pending] : acc),
								[]
							)
						)
					);
				})
				.catch(() => notifyDanger(ITEM_ADDED_TO_CART_FAIL));
		},
		addToCartLoading
	};
};

const buildProjectsAddToCartAnalytics = (items: (ProjectDetailsGroupItem | ProjectItemFieldsFragment)[] | null) => {
	return {
		event: TrackedEvent.ADD_TO_CART,
		type: TrackedEventCase.ADD_TO_CART_PROJECTS,
		ecommerce: {
			add: {
				products:
					items &&
					items.map((item) => ({
						id: String(item.productVariantId),
						name: `${item.manufacturer} ${item.productId}`,
						category: item.baseCategory,
						variant: item.finish,
						brand: item.manufacturer,
						price: item.unitPrice,
						quantity: item.quantityUnPurchased,
						familyId: item.familyId,
						[GTMCustomProductDimensions.PRODUCT_APPLICATION]: item.application,
						[GTMCustomProductDimensions.IS_OUT_OF_STOCK]: item.stockCount === 0,
						[GTMCustomProductDimensions.HAS_IMAGE]: Boolean(item.image?.id),
						[GTMCustomProductDimensions.PRODUCT_ID]: item.productId,
						[GTMCustomProductDimensions.ADD_TO_CART_METHOD]: TrackedEventCase.ADD_TO_CART_PROJECTS
					}))
			},
			cartSummary: {
				items:
					items &&
					items.map((item) => ({
						uniqueId: item.productVariantId,
						productQuantity: item.quantityUnPurchased,
						unitPrice: item.unitPrice
					}))
			}
		}
	};
};
